// components/LiveController.js - Optimized WebSocket Synchronization
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { StartDialog } from './StartDialog';
import {
  ArrowLeft,
  Play,
  Pause,
  RotateCcw,
  Clock,
  AlertTriangle,
  CheckCircle,
  Share2
} from 'lucide-react';
import {
  getCompetitionById,
  createTimer,
  startCountdown,
  startClimbing,
  startWrapTime,
  pauseTimer,
  resumeTimer,
  resetTimer,
  getTimerState,
  getJudges
} from '../services/apiServices';
import socketService from '../services/socketService';
import { ShareLinksDialog } from './ShareLinksDialog';

// Helper function to compute remaining time from currentEndTime
const computeRemainingFromEndTime = (timer) => {
  if (!timer?.currentEndTime) return 0;

  const now = Date.now();
  const end = new Date(timer.currentEndTime).getTime();

  const diffInMs = end - now;
  const diffInSeconds = Math.max(0, Math.ceil(diffInMs / 1000));

  return diffInSeconds;
};

export const LiveController = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [competition, setCompetition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);

  // Timer states
  const [timerState, setTimerState] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [timerError, setTimerError] = useState(null);
  const [localRemainingTime, setLocalRemainingTime] = useState(0);
  const [judges, setJudges] = useState([]);

  // Refs for interval and tracking
  const timerIntervalRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  const isMountedRef = useRef(true);
  const currentTimeRef = useRef(Date.now());
  const autoTransitionInProgressRef = useRef(false);
  const lastTimerStateRef = useRef(null);
  const lastLocalTimeRef = useRef(0);
  const animationFrameRef = useRef(null);

  // Set initial idle state
  const setInitialIdleState = useCallback(() => {
    const idleState = {
      status: 'idle',
      remainingTime: 0,
      currentPhase: 'countdown',
      currentRound: 1,
      totalRounds: competition?.totalRounds || 1
    };
    setTimerState(idleState);
    setLocalRemainingTime(0);
    stopLocalTimer();
  }, [competition?.totalRounds]);

  // Memoized timer update handler
  const handleTimerUpdate = useCallback((newTimerState) => {
    if (!isMountedRef.current) return;

    if (!newTimerState) {
      console.log('Received null timer state, setting to idle');
      setInitialIdleState();
      return;
    }

    console.log('Timer update received:', {
      status: newTimerState.status,
      currentPhase: newTimerState.currentPhase,
      remainingTime: newTimerState.remainingTime,
      currentEndTime: newTimerState.currentEndTime,
      timestamp: new Date().toISOString()
    });

    // Update last sync time
    lastUpdateTimeRef.current = Date.now();
    currentTimeRef.current = Date.now();

    // Reset auto-transition flag
    autoTransitionInProgressRef.current = false;

    // Store last state for comparison
    lastTimerStateRef.current = newTimerState;

    // Update states
    setTimerState(newTimerState);

    // Calculate remaining time from currentEndTime for better accuracy
    const computedRemaining = computeRemainingFromEndTime(newTimerState);
    lastLocalTimeRef.current = computedRemaining;

    // If timer is paused, set to 0 immediately
    if (newTimerState.status === 'paused') {
      setLocalRemainingTime(0);
      stopLocalTimer();
    } else {
      setLocalRemainingTime(computedRemaining);
      // Start smooth local timer
      manageLocalTimer(newTimerState);
    }

    setTimerError(null);
  }, [setInitialIdleState]);

  // Stop local countdown timer
  const stopLocalTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    console.log('⏹️ Local countdown stopped');
  };

  // Smooth timer update using requestAnimationFrame
  const startSmoothTimer = useCallback((timerData) => {
    stopLocalTimer();

    if (!timerData?.currentEndTime || timerData.status === 'paused') {
      return;
    }

    const startTime = Date.now();
    const endTime = new Date(timerData.currentEndTime).getTime();
    const duration = endTime - startTime;

    if (duration <= 0) return;

    const updateTimer = () => {
      if (!isMountedRef.current) return;

      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const remainingMs = Math.max(0, duration - elapsed);
      const remainingSeconds = Math.ceil(remainingMs / 1000);

      setLocalRemainingTime(remainingSeconds);

      if (remainingMs > 0) {
        animationFrameRef.current = requestAnimationFrame(updateTimer);
      } else {
        // Timer reached zero
        console.log('⏰ Smooth timer reached zero');
        autoTransitionOnZero(0);
        stopLocalTimer();
      }
    };

    console.log('▶️ Starting smooth timer for:', timerData.status, 'remaining:', Math.ceil(duration / 1000));
    animationFrameRef.current = requestAnimationFrame(updateTimer);
  }, []);

  // Auto transition when timer reaches zero
  const autoTransitionOnZero = useCallback(async (remainingTime) => {
    if (remainingTime > 0 || loading || !timerState || autoTransitionInProgressRef.current) return;

    const { status, currentRound, totalRounds } = timerState;

    console.log('🔄 Auto-transition check:', { status, currentRound, totalRounds, remainingTime });

    // Prevent multiple simultaneous transitions
    autoTransitionInProgressRef.current = true;

    try {
      // Only auto-transition for active phases
      if (status === 'countdown') {
        console.log('🔄 Auto-transition: countdown → climbing');
        await handleStartClimbing();
      } else if (status === 'climbing') {
        console.log('🔄 Auto-transition: climbing → wrap_time');
        await handleStartWrapTime();
      } else if (status === 'wrap_time' && currentRound < totalRounds) {
        console.log('🔄 Auto-transition: wrap_time → climbing (next round)');
        await handleStartClimbing();
      }
    } catch (error) {
      console.error('Auto-transition error:', error);
      setTimerError('Auto-transition failed: ' + error.message);
      autoTransitionInProgressRef.current = false;
    }
  }, [timerState, loading]);

  // Manage local timer countdown
  const manageLocalTimer = useCallback((timerData) => {
    if (!timerData) {
      stopLocalTimer();
      return;
    }

    const { status, currentEndTime } = timerData;

    // Stop existing timer
    stopLocalTimer();

    // Only run local countdown for active phases with time remaining
    if (status && ['countdown', 'climbing', 'wrap_time'].includes(status)) {
      const computedRemaining = computeRemainingFromEndTime(timerData);
      if (computedRemaining > 0) {
        console.log('▶️ Starting timer for:', status, 'remaining:', computedRemaining);
        startSmoothTimer(timerData);
      } else {
        console.log('⏸️ Not starting timer - zero or negative time:', {
          status,
          computedRemaining,
          currentEndTime
        });
      }
    } else {
      console.log('⏸️ Not starting timer for status:', status);
    }
  }, [startSmoothTimer]);
  console.log(timerState, 'testing');

  // Initialize socket connection with proper cleanup
  useEffect(() => {
    isMountedRef.current = true;
    let reconnectTimeout = null;

    const initializeSocket = async () => {
      if (!competition?._id) return;

      try {
        console.log('🔄 Initializing WebSocket connection...');

        // Connect to WebSocket
        await socketService.connect();

        if (!isMountedRef.current) return;

        // Join competition with user info
        const userInfo = {
          userId: JSON.parse(localStorage.getItem("loggedAdminInfo"))?.id || 'admin',
          userType: 'admin'
        };

        const joined = socketService.joinCompetition(competition._id, userInfo);

        if (joined) {
          setIsSocketConnected(true);
          console.log('✅ Successfully joined competition:', competition._id);

          // Request initial timer state
          socketService.getTimerState(competition._id);
        } else {
          throw new Error('Failed to join competition');
        }

        // Set up event listeners with proper cleanup
        const timerUpdateHandler = (data) => {
          if (isMountedRef.current && data) {
            handleTimerUpdate(data);
          }
        };

        const timerErrorHandler = (error) => {
          if (isMountedRef.current) {
            console.error('WebSocket timer error:', error);
            setTimerError(error.message || 'WebSocket error');
          }
        };

        socketService.onTimerUpdate(timerUpdateHandler);
        socketService.onTimerError(timerErrorHandler);

        // Store handlers for cleanup
        return { timerUpdateHandler, timerErrorHandler };

      } catch (error) {
        console.error('❌ WebSocket connection error:', error);
        if (isMountedRef.current) {
          setTimerError('Failed to connect to live updates');
          setIsSocketConnected(false);

          // Retry connection after 5 seconds
          reconnectTimeout = setTimeout(() => {
            console.log('🔄 Retrying connection...');
            initializeSocket();
          }, 5000);
        }
      }
    };

    const handlers = initializeSocket();

    // Cleanup function
    return () => {
      isMountedRef.current = false;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      // Clean up socket listeners
      socketService.offTimerUpdate();
      socketService.offTimerError();

      // Leave competition and disconnect
      if (competition?._id) {
        socketService.leaveCompetition(competition._id);
      }

      stopLocalTimer();

      console.log('🧹 Cleaned up WebSocket connection');
    };
  }, [competition?._id, handleTimerUpdate]);

  // Fetch competition data on mount
  useEffect(() => {
    async function fetchCompetition() {
      try {
        const response = await getCompetitionById(location.state?._id);
        if (!response || response === "false") return;

        const competitionData = await response.json();
        setCompetition(competitionData);

        // Fetch initial timer state
        await checkAndFetchTimerState(competitionData._id);
      } catch (error) {
        console.error("Error fetching competition:", error);
      }
    }

    if (location.state?._id) {
      fetchCompetition();
    }
  }, [location.state]);

  // Single polling mechanism - only when WebSocket is disconnected
  useEffect(() => {
    if (!competition?._id || isSocketConnected) return;

    let pollingInterval = null;
    let pollingTimeout = null;

    const pollTimerState = async () => {
      try {
        const response = await getTimerState(competition._id);
        if (response !== "false") {
          const data = await response.json();
          if (data.success && data.data) {
            handleTimerUpdate(data.data);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }

      // Adaptive polling based on remaining time
      const nextPollTime = localRemainingTime <= 10 ? 1000 : 3000;
      pollingTimeout = setTimeout(() => {
        if (!isSocketConnected && competition?._id) {
          pollingInterval = requestAnimationFrame(pollTimerState);
        }
      }, nextPollTime);
    };

    // Start polling
    pollingInterval = requestAnimationFrame(pollTimerState);

    return () => {
      if (pollingInterval) {
        cancelAnimationFrame(pollingInterval);
      }
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [competition?._id, isSocketConnected, localRemainingTime, handleTimerUpdate]);

  // Fetch judges
  useEffect(() => {
    async function fetchJudges() {
      try {
        const organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo"))?.organizerId || "";
        const judgeResponse = await getJudges(organiserId);

        if (judgeResponse && judgeResponse !== "false") {
          const judgeData = await judgeResponse.json();
          setJudges(judgeData || []);
        }
      } catch (error) {
        console.error("Error fetching judges:", error);
      }
    }

    fetchJudges();
  }, []);

  // Check if timer exists and fetch initial state
  const checkAndFetchTimerState = async (competitionId) => {
    try {
      const response = await getTimerState(competitionId);
      if (response !== "false") {
        const data = await response.json();
        if (data.success && data.data) {
          handleTimerUpdate(data.data);
        } else {
          setInitialIdleState();
        }
      } else {
        setInitialIdleState();
      }
    } catch (error) {
      console.error('Error fetching timer state:', error);
      setInitialIdleState();
    }
  };

  // Start countdown with proper error handling
  const handleStartCountdown = async (countdownSeconds) => {
    setLoading(true);
    setTimerError(null);

    try {
      const update = {
        countdown: countdownSeconds,
        climbTime: competition.climbTime,
        warningTime: competition.warningTime,
        wrapTime: competition.wrapTime,
        organizerId: competition.organizerId,
      };
      console.log(update, 'updateh')

      // Create timer
      const createResponse = await createTimer(competition._id, update);
      if (createResponse === "false") {
        throw new Error('Failed to create timer');
      }

      const createData = await createResponse.json();
      if (!createData.success) {
        throw new Error(createData.message || 'Failed to create timer');
      }

      // Small delay to ensure backend is ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Start countdown
      const countdownResponse = await startCountdown(competition._id);
      if (countdownResponse === "false") {
        throw new Error('Failed to start countdown');
      }

      const countdownData = await countdownResponse.json();
      if (countdownData.success) {
        handleTimerUpdate(countdownData.data);
      } else {
        throw new Error(countdownData.message || 'Failed to start countdown');
      }

    } catch (error) {
      console.error('Error in handleStartCountdown:', error);
      setTimerError(error.message);
    } finally {
      setLoading(false);
      setShowStartDialog(false);
    }
  };

  // Generic API call handler with immediate follow-up
  const handleApiCall = async (apiFunction, errorMessage) => {
    setLoading(true);
    setTimerError(null);

    try {
      const response = await apiFunction(competition._id);
      if (response !== "false") {
        const data = await response.json();
        if (data.success) {
          handleTimerUpdate(data.data);

          // Immediate follow-up request to get updated state
          setTimeout(() => {
            if (competition?._id && socketService.getConnectionStatus()) {
              console.log('🔄 Immediate follow-up after API call');
              socketService.getTimerState(competition._id);
            } else {
              // Fallback to HTTP
              getTimerState(competition._id).then(res => {
                if (res !== "false") {
                  return res.json();
                }
              }).then(data => {
                if (data?.success) {
                  handleTimerUpdate(data.data);
                }
              });
            }
          }, 100);
        } else {
          throw new Error(data.message || errorMessage);
        }
      } else {
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error(`API call error:`, error);
      setTimerError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartClimbing = () => handleApiCall(startClimbing, 'Failed to start climbing');
  const handleStartWrapTime = () => handleApiCall(startWrapTime, 'Failed to start wrap time');
  const handleResumeTimer = () => handleApiCall(resumeTimer, 'Failed to resume timer');
  const handleResetTimer = () => handleApiCall(resetTimer, 'Failed to reset timer');

  const handlePauseTimer = async () => {
    setLoading(true);
    setTimerError(null);

    try {
      const response = await pauseTimer(competition._id, "Admin manual pause", "Admin");
      if (response !== "false") {
        const data = await response.json();
        if (data.success) {
          handleTimerUpdate(data.data);
          stopLocalTimer();
          // Set local timer to 0 immediately when paused
          setLocalRemainingTime(0);
        } else {
          throw new Error(data.message || 'Failed to pause timer');
        }
      } else {
        throw new Error('Failed to pause timer');
      }
    } catch (error) {
      console.error('Pause error:', error);
      setTimerError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getJudgeName = (judgeId) => {
    const judge = judges.find(j => j._id === judgeId);
    return judge ? `${judge.firstname} ${judge.lastname}` : 'Unknown Judge';
  };
  console.log(timerState?.currentPhase, "climbing", "warning");

  // Get phase display information
  const getPhaseInfo = () => {
    if (!timerState) {
      return {
        color: 'bg-gray-500',
        display: 'READY',
        icon: Clock,
        message: 'Ready to start',
        cardColor: 'border-gray-500 bg-gray-50'
      };
    }

    const { currentPhase, status } = timerState;
    const remainingTime = localRemainingTime;
    const warningTime = competition?.warningTime || 60;

    // Handle paused state
    if (status === 'paused') {
      return {
        color: 'bg-yellow-500',
        display: 'PAUSED',
        icon: Pause,
        message: 'Timer is paused. Click "Resume" to continue.',
        cardColor: 'border-yellow-500 bg-yellow-50'
      };
    }

    // Handle completed state
    if (status === 'completed') {
      return {
        color: 'bg-purple-500',
        display: 'COMPLETED',
        icon: CheckCircle,
        message: 'Competition completed!',
        cardColor: 'border-purple-500 bg-purple-50'
      };
    }

    // Handle idle state
    if (status === 'idle') {
      return {
        color: 'bg-gray-500',
        display: 'READY',
        icon: Clock,
        message: 'Ready to start',
        cardColor: 'border-gray-500 bg-gray-50'
      };
    }

    // Handle active phases
    switch (currentPhase) {
      case 'countdown':
        return {
          color: 'bg-blue-500',
          display: 'COUNTDOWN',
          icon: Clock,
          message: remainingTime > 0 ? `Countdown: ${formatTime(remainingTime)}` : 'Countdown starting...',
          cardColor: 'border-blue-500 bg-blue-50'
        };
      case 'climbing':
        if (remainingTime <= warningTime && remainingTime > 0) {
          return {
            color: 'bg-orange-500',
            display: 'WARNING',
            icon: AlertTriangle,
            message: `Warning! ${formatTime(remainingTime)} remaining`,
            cardColor: 'border-orange-500 bg-orange-50'
          };
        }
        return {
          color: 'bg-green-500',
          display: 'CLIMBING',
          icon: Play,
          message: remainingTime > 0 ? `Climbing: ${formatTime(remainingTime)} remaining` : 'Climbing time...',
          cardColor: 'border-green-500 bg-green-50'
        };
      case 'warning':
        return {
          color: 'bg-orange-500',
          display: 'WARNING',
          icon: AlertTriangle,
          message: `Warning! ${formatTime(remainingTime)} remaining`,
          cardColor: 'border-orange-500 bg-orange-50'
        };
      case 'wrap_time':
        return {
          color: 'bg-purple-500',
          display: 'WRAP TIME',
          icon: AlertTriangle,
          message: remainingTime > 0 ? `Wrap time: ${formatTime(remainingTime)} remaining` : 'Wrap time...',
          cardColor: 'border-purple-500 bg-purple-50'
        };
      default:
        return {
          color: 'bg-gray-500',
          display: 'READY',
          icon: Clock,
          message: 'Ready to start',
          cardColor: 'border-gray-500 bg-gray-50'
        };
    }
  };

  const phaseInfo = getPhaseInfo();
  const PhaseIcon = phaseInfo.icon;

  // Derived states
  const currentStatus = timerState?.status || 'idle';
  const isIdle = currentStatus === 'idle';
  const isCountdown = currentStatus === 'countdown';
  const isClimbing = currentStatus === 'climbing';
  const isWrapTime = currentStatus === 'wrap_time';
  const isPaused = currentStatus === 'paused';
  const isCompleted = currentStatus === 'completed';

  const canStart = isIdle;
  const canPause = (isCountdown || isClimbing || isWrapTime) && !isPaused;
  const canResume = isPaused;

  // Loading state
  if (!competition) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-2xl mx-auto flex items-center justify-center">
        <Card className="bg-white border-gray-200 shadow-lg w-full">
          <CardContent className="p-6">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-blue-700 text-center">
                Please create a competition first.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate('/create-round')}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium h-11"
            >
              Create Competition
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 max-w-4xl mx-auto pb-24">
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <ArrowLeft className="size-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900">{competition.name}</h1>
            <p className="text-gray-600 mt-1">Live Controller</p>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={isSocketConnected ? "default" : "secondary"}
                className={isSocketConnected ? "bg-green-500" : "bg-gray-500"}>
                {isSocketConnected ? "🟢 Live" : "🔴 Offline"}
              </Badge>
              <Badge variant="outline" className="text-xs">
                Sync: {isSocketConnected ? 'WebSocket' : 'HTTP Polling'}
              </Badge>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowShareDialog(true)}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <Share2 className="size-4 mr-2" />
            Share
          </Button>
          <Badge className={phaseInfo.color}>
            {phaseInfo.display}
          </Badge>
        </div>

        {/* Error Display */}
        {/* {timerError && (
          <Alert className="bg-red-50 border-red-200">
            <AlertDescription className="text-red-700">
              <strong>Timer Error:</strong> {timerError}
            </AlertDescription>
          </Alert>
        )} */}

        {/* Main Timer Display */}
        <Card className={phaseInfo.cardColor}>
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-2">
                <PhaseIcon className="size-8" />
                <p className="text-2xl font-bold">{phaseInfo.display}</p>
              </div>

              <p className="text-6xl tabular-nums font-bold">
                {formatTime(localRemainingTime)}
              </p>

              <p className="text-lg">{phaseInfo.message}</p>

              {/* Round Info */}
              {timerState && timerState.status !== 'idle' && (
                <div className="mt-4 p-4 bg-white rounded-lg border">
                  <p className="text-sm font-medium">Current Round</p>
                  <p className="text-xl font-bold">Round {timerState.currentRound || 1}</p>
                  <div className="flex justify-center gap-4 mt-2">
                    <Badge className="bg-gray-100 text-gray-800">
                      Total Rounds: {timerState.totalRounds || 1}
                    </Badge>
                    {timerState.currentEndTime && timerState.status !== 'paused' && (
                      <Badge className="bg-blue-100 text-blue-800">
                        Ends: {new Date(timerState.currentEndTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Control Panel */}
        {!isCompleted && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-gray-900">Timer Controls</CardTitle>
              <p className="text-sm text-gray-600">
                {isSocketConnected ? "Live mode - real-time updates" : "HTTP polling every 3s"}
              </p>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-4">
              {canStart && (
                <Button
                  onClick={() => setShowStartDialog(true)}
                  className="flex-1 min-w-[140px] bg-blue-600 hover:bg-blue-700 text-white font-medium h-12"
                  disabled={loading}
                >
                  <Play className="size-4 mr-2" />
                  Start Timer
                </Button>
              )}

              {canPause && (
                <Button
                  onClick={handlePauseTimer}
                  className="flex-1 min-w-[140px] h-12 font-medium bg-yellow-600 hover:bg-yellow-700 text-white"
                  disabled={(timerState?.currentPhase == "climbing" || timerState?.currentPhase == "warning") || loading}
                >
                  <Pause className="size-4 mr-2" />
                  Pause
                </Button>
              )}

              {canResume && (
                <Button
                  onClick={handleResumeTimer}
                  className="flex-1 min-w-[140px] h-12 font-medium bg-green-600 hover:bg-green-700 text-white"
                  disabled={loading}
                >
                  <Play className="size-4 mr-2" />
                  Resume
                </Button>
              )}

              {/* {!isIdle && (
                <Button
                  onClick={handleResetTimer}
                  className="flex-1 min-w-[140px] h-12 font-medium bg-red-600 hover:bg-red-700 text-white"
                  disabled={loading}
                >
                  <RotateCcw className="size-4 mr-2" />
                  Reset
                </Button>
              )} */}
            </CardContent>
          </Card>
        )}

        {/* Progress Information */}
        {timerState && timerState.status !== 'idle' && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl text-gray-900">Timer Progress</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Current Round</p>
                <p className="text-2xl font-bold text-gray-900">{timerState.currentRound || 1}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Total Rounds</p>
                <p className="text-2xl font-bold text-gray-900">{timerState.totalRounds || 1}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Current Phase</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">{timerState.currentPhase || 'idle'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Status</p>
                <p className="text-2xl font-bold text-gray-900 capitalize">{timerState.status || 'idle'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Boulder Status Cards */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">Boulder Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {competition.boulders && competition.boulders.map(boulder => {
              const assignedJudge = boulder.judgeId ? getJudgeName(boulder.judgeId) : null;

              return (
                <Card key={boulder._id} className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg text-gray-900">{boulder.name}</CardTitle>
                      {assignedJudge ? (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          {assignedJudge}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                          No Judge
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Competition Info */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-gray-900">Competition Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Climb Time</p>
              <p className="text-2xl font-bold text-gray-900">{competition.climbTime || 240}s</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Wrap Time</p>
              <p className="text-2xl font-bold text-gray-900">{competition.wrapTime || 30}s</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Warning Time</p>
              <p className="text-2xl font-bold text-gray-900">{competition.warningTime || 60}s</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">Boulders</p>
              <p className="text-2xl font-bold text-gray-900">{competition.numberofboulders || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <StartDialog
        open={showStartDialog}
        onOpenChange={setShowStartDialog}
        onStart={handleStartCountdown}
      />

      <ShareLinksDialog
        open={showShareDialog}
        onOpenChange={setShowShareDialog}
        shareableId={competition._id}
      />
    </div>
  );
};